import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Plus, Trash, Eye, EyeSlash, UploadSimple, X, ImageBroken } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL;

const MutuellesAdminPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputs = useRef({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/api/admin/mutuelles`, { withCredentials: true });
      setItems(data || []);
    } catch (e) {
      toast.error('Impossible de charger les mutuelles');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/mutuelles`, { name: newName.trim() }, { withCredentials: true });
      setItems(prev => [...prev, data]);
      setNewName('');
      toast.success('Mutuelle ajoutée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setCreating(false); }
  };

  const update = async (id, patch) => {
    try {
      const { data } = await axios.put(`${API}/api/admin/mutuelles/${id}`, patch, { withCredentials: true });
      setItems(prev => prev.map(m => m.id === id ? data : m));
    } catch (e) {
      toast.error('Erreur de mise à jour');
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer la mutuelle "${name}" ?`)) return;
    try {
      await axios.delete(`${API}/api/admin/mutuelles/${id}`, { withCredentials: true });
      setItems(prev => prev.filter(m => m.id !== id));
      toast.success('Mutuelle supprimée');
    } catch (e) {
      toast.error('Erreur de suppression');
    }
  };

  const uploadLogo = async (id, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Fichier image requis (PNG/JPG/SVG)');
    if (file.size > 2 * 1024 * 1024) return toast.error('Taille max 2 Mo');
    setUploadingId(id);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', 'mutuelles');
    try {
      const { data } = await axios.post(`${API}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = data.url || data.path || data.file_url;
      if (!url) throw new Error('URL manquante');
      await update(id, { logo_url: url });
      toast.success('Logo mis à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload échoué');
    } finally { setUploadingId(null); }
  };

  const seedDefaults = async () => {
    if (!window.confirm('Insérer les 14 mutuelles par défaut (Almerys, Itelis, Santéclair…) ?')) return;
    try {
      const { data } = await axios.post(`${API}/api/admin/mutuelles/seed`, {}, { withCredentials: true });
      toast.success(`${data.inserted} mutuelle(s) ajoutée(s)`);
      fetchData();
    } catch (e) {
      toast.error('Erreur de seed');
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1100px] mx-auto space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck size={26} weight="duotone" className="text-[#F9055B]" />Mutuelles partenaires</h1>
          <p className="text-sm text-slate-500 mt-1">Les mutuelles affichées dans le bandeau du site (tiers payant).</p>
        </div>
        <div className="flex items-center gap-2">
          {items.length === 0 && !loading && (
            <Button variant="outline" size="sm" onClick={seedDefaults} data-testid="seed-mutuelles">
              ⚡ Ajouter les 14 par défaut
            </Button>
          )}
        </div>
      </header>

      {/* Add new */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 flex items-center gap-3">
        <Input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && create()}
          placeholder="Nom de la mutuelle (ex: Almerys, Santéclair…)"
          className="flex-1"
          data-testid="new-mutuelle-input"
        />
        <Button onClick={create} disabled={creating || !newName.trim()} className="bg-[#F9055B] hover:bg-[#d1004c]" data-testid="new-mutuelle-add">
          <Plus size={16} className="mr-1.5" /> Ajouter
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-8 text-slate-400">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-xl border border-dashed border-slate-200">
          <ShieldCheck size={40} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 mb-3">Aucune mutuelle configurée</p>
          <Button variant="outline" size="sm" onClick={seedDefaults}>⚡ Ajouter les 14 par défaut</Button>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((m) => (
            <div key={m.id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-4" data-testid={`mutuelle-row-${m.slug}`}>
              {/* Logo cell */}
              <div className="relative w-16 h-12 bg-slate-50 rounded-md border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                {m.logo_url ? (
                  <>
                    <img src={m.logo_url} alt={m.name} className="max-w-full max-h-full object-contain" />
                    <button
                      onClick={() => update(m.id, { logo_url: '' })}
                      className="absolute top-0 right-0 w-4 h-4 bg-slate-900/70 hover:bg-red-600 text-white text-[10px] rounded-bl flex items-center justify-center transition-colors"
                      title="Retirer le logo"
                    >
                      <X size={10} />
                    </button>
                  </>
                ) : (
                  <ImageBroken size={20} className="text-slate-300" />
                )}
              </div>

              {/* Name */}
              <Input
                value={m.name}
                onChange={e => setItems(prev => prev.map(x => x.id === m.id ? { ...x, name: e.target.value } : x))}
                onBlur={() => update(m.id, { name: m.name })}
                className="flex-1"
                data-testid={`mutuelle-name-${m.slug}`}
              />

              {/* Upload */}
              <input
                ref={el => fileInputs.current[m.id] = el}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={e => uploadLogo(m.id, e.target.files?.[0])}
              />
              <Button
                variant="outline" size="sm"
                onClick={() => fileInputs.current[m.id]?.click()}
                disabled={uploadingId === m.id}
                className="gap-1.5"
                data-testid={`mutuelle-upload-${m.slug}`}
              >
                <UploadSimple size={14} /> {uploadingId === m.id ? 'Upload…' : 'Logo'}
              </Button>

              {/* Visibility */}
              <div className="flex items-center gap-2">
                {m.is_active ? <Eye size={16} className="text-emerald-600" /> : <EyeSlash size={16} className="text-amber-500" />}
                <Switch
                  checked={m.is_active}
                  onCheckedChange={(v) => update(m.id, { is_active: v })}
                  data-testid={`mutuelle-active-${m.slug}`}
                />
              </div>

              {/* Delete */}
              <Button variant="ghost" size="sm" onClick={() => remove(m.id, m.name)} className="text-red-500 hover:bg-red-50">
                <Trash size={15} />
              </Button>
            </div>
          ))}
        </div>
      )}
      <p className="text-[12px] text-slate-500 pt-2">
        💡 Les mutuelles affichées en gris sont masquées du site mais conservées en BDD. Logos recommandés : PNG/SVG, fond transparent, hauteur ~60px.
      </p>
    </div>
  );
};

export default MutuellesAdminPage;
